import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { ABOUT } from "@/i18n/about_data";
import {
  ArrowRight, Compass, Layers, Network, ShieldCheck, Lock, CheckCircle2, XCircle,
  MapPin, Clock, AlertTriangle, FileSearch, Route, Users, Sparkles,
} from "lucide-react";

const stageIcons = [Compass, Layers, Network, ShieldCheck];
const outputIcons = [MapPin, Clock, AlertTriangle, FileSearch, Route, Users];
const principleIcons = [Sparkles, Route, Layers, Lock];

function H({ children, id }) {
  return <h2 id={id} className="text-3xl md:text-4xl font-bold text-slate-50 leading-tight">{children}</h2>;
}

export default function About() {
  const { lang } = useLang();
  const A = ABOUT[lang] || ABOUT.en;

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-mift-hero relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-20">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-blue-300 bg-blue-500/10 border border-blue-500/30 px-3 py-1 rounded-full">
            {A.hero.eyebrow}
          </span>
          <p className="mt-3 text-sm text-slate-400 max-w-3xl">{A.hero.subtitle}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-tight max-w-4xl">
            {A.hero.headline}
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-3xl leading-relaxed">{A.hero.support}</p>
          <p className="mt-4 text-sm text-blue-300">{A.hero.audience}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/framework" data-testid="about-cta-framework" className="btn-primary inline-flex items-center gap-2">
              {A.hero.cta1} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/assessment" data-testid="about-cta-assessment" className="btn-ghost">{A.hero.cta2}</Link>
            <Link to="/toolkit" data-testid="about-cta-toolkit" className="btn-ghost">{A.hero.cta3}</Link>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <H>{A.problem.h}</H>
        <div className="mt-6 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-4 text-slate-300 leading-relaxed">
            <p>{A.problem.p1}</p>
            <p>{A.problem.p2}</p>
            <p className="text-blue-300 font-semibold">{A.problem.highlight}</p>
          </div>
          <div className="lg:col-span-2 card-mift p-6 relative">
            <div className="text-xs uppercase tracking-widest text-blue-300 mb-3">Insight</div>
            <p className="text-slate-100 leading-relaxed">{A.problem.insight}</p>
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-mift p-6" data-testid="compare-without">
            <div className="flex items-center gap-2 text-slate-300">
              <XCircle className="w-5 h-5 text-rose-400" />
              <h3 className="font-semibold">{A.compare.h_left}</h3>
            </div>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-400">
              {A.compare.left.map((it, i) => (
                <li key={i} className="flex gap-2"><span className="text-rose-400/70">×</span>{it}</li>
              ))}
            </ul>
          </div>
          <div className="card-mift p-6 border-blue-500/40" data-testid="compare-with">
            <div className="flex items-center gap-2 text-blue-300">
              <CheckCircle2 className="w-5 h-5" />
              <h3 className="font-semibold">{A.compare.h_right}</h3>
            </div>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-200">
              {A.compare.right.map((it, i) => (
                <li key={i} className="flex gap-2"><span className="text-blue-400">→</span>{it}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-6 text-sm text-slate-400 italic max-w-3xl">{A.compare.note}</p>
      </section>

      {/* HOW MIFT WORKS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.how.h}</H>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-3xl">{A.how.intro}</p>

        <div className="mt-10 hidden md:block relative">
          <div className="absolute left-0 right-0 top-8 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
          <div className="grid grid-cols-4 relative z-10">
            {A.stages.map((s) => (
              <div key={s.n} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full stage-badge flex items-center justify-center font-bold text-blue-300">{s.n}</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-blue-300">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {A.stages.map((s, i) => {
            const Ic = stageIcons[i];
            return (
              <div key={s.n} className="card-mift p-6" data-testid={`about-stage-${s.n}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full stage-badge flex items-center justify-center font-semibold">{s.n}</div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-300">{A.labels.stage} · {s.label}</div>
                    <div className="text-slate-100 font-semibold">{s.title}</div>
                  </div>
                  <Ic className="ml-auto w-5 h-5 text-blue-300/70" />
                </div>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed">{s.desc}</p>
                <div className="mt-4 text-xs uppercase tracking-wider text-blue-300">{A.labels.key_questions}</div>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-300">
                  {s.qs.map((q, j) => <li key={j} className="flex gap-2"><span className="text-blue-400 flex-shrink-0">→</span>{q}</li>)}
                </ul>
                <div className="mt-5 inline-block text-xs px-3 py-1 rounded-full border border-blue-500/40 text-blue-300 bg-blue-500/10">
                  {A.labels.output}: {s.out}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-sm text-blue-300 italic max-w-3xl">{A.how.after}</p>
      </section>

      {/* OUTPUTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.outputs.h}</H>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {A.outputs.cards.map((c, i) => {
            const Ic = outputIcons[i] || MapPin;
            return (
              <div key={i} className="card-mift p-6" data-testid={`about-output-${i}`}>
                <Ic className="w-6 h-6 text-blue-300" />
                <div className="mt-4 font-semibold text-slate-100">{c.t}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.d}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-xs text-slate-500 italic max-w-3xl">{A.outputs.note}</p>
      </section>

      {/* IMPROVE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.improve.h}</H>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-3xl">{A.improve.intro}</p>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {A.improve.cards.map((c, i) => (
            <div key={i} className="card-mift p-5" data-testid={`about-improve-${i}`}>
              <div className="font-semibold text-slate-100">{c.t}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.principles.h}</H>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {A.principles.items.map((p, i) => {
            const Ic = principleIcons[i];
            return (
              <div key={i} className="card-mift p-6" data-testid={`about-principle-${i}`}>
                <Ic className="w-6 h-6 text-blue-300" />
                <div className="mt-4 font-semibold text-slate-100">{p.t}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* APPLY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.apply.h}</H>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {A.apply.cards.map((c, i) => (
            <div key={i} className="card-mift p-5" data-testid={`about-apply-${i}`}>
              <div className="font-semibold text-slate-100 leading-snug">{c.t}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500 italic max-w-3xl">{A.apply.note}</p>
      </section>

      {/* PRACTICE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.practice.h}</H>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-3xl">{A.practice.intro}</p>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {A.practice.cards.map((c, i) => (
            <div key={i} className="card-mift p-6 flex flex-col" data-testid={`about-practice-${i}`}>
              <div className="font-semibold text-slate-100 leading-snug">{c.t}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed flex-1">{c.d}</p>
              <Link to={c.to} className="mt-5 inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-sm">
                {c.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* IMPLEMENTATION */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.implementation.h}</H>
        <div className="mt-6 grid lg:grid-cols-2 gap-8 text-slate-300 leading-relaxed">
          <p>{A.implementation.p1}</p>
          <p>{A.implementation.p2}</p>
        </div>
        <div className="mt-8 card-mift p-6" data-testid="about-status">
          <div className="text-xs uppercase tracking-widest text-blue-300 mb-3">Status</div>
          <p className="text-slate-300 text-sm leading-relaxed">{A.implementation.status}</p>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <H>{A.why.h}</H>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-3xl">{A.why.p}</p>
        <div className="mt-8 card-mift p-6">
          <ul className="grid gap-3 md:grid-cols-2 text-sm text-slate-200">
            {A.why.checklist.map((q, i) => (
              <li key={i} className="flex gap-2"><span className="text-blue-400">?</span>{q}</li>
            ))}
          </ul>
        </div>
        <p className="mt-6 text-blue-300 font-medium">{A.why.highlight}</p>
      </section>

      {/* SCOPE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <div className="card-mift p-6">
          <h3 className="text-lg font-semibold text-slate-100">{A.scope.h}</h3>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">{A.scope.p1}</p>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">{A.scope.p2}</p>
          <Link to="/disclaimer" className="mt-4 inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-sm">
            {A.scope.link} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <div className="card-mift p-8 md:p-10">
          <H>{A.final.h}</H>
          <p className="mt-4 text-slate-300">{A.final.p}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/assessment" className="btn-primary" data-testid="about-final-assessment">{A.final.cta1}</Link>
            <Link to="/framework" className="btn-ghost" data-testid="about-final-framework">{A.final.cta2}</Link>
            <Link to="/pilot" className="btn-ghost" data-testid="about-final-pilot">{A.final.cta3}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
